'use client';

import React, { useState } from 'react';

type SchemeCardData = {
  id: number;
  title: string;
  description: string;
  tag: string;
  imgBg: string;
  imgAccent: string;
};

const schemeCards: SchemeCardData[] = [
  {
    id: 1,
    title: 'NNAIFF Funding',
    description: 'State-backed financial assistance designed to accelerate the scaling operations of MSMEs and emergent startups.',
    tag: 'MSME & STARTUP',
    imgBg: 'from-[#dbeafe] to-[#bfdbfe]',
    imgAccent: '#0060A9',
  },
  {
    id: 2,
    title: 'NGO Elevation Program',
    description: 'Comprehensive financial advisory and regulatory compliance support tailored for non-governmental organizations.',
    tag: 'NON-PROFIT',
    imgBg: 'from-[#fef3c7] to-[#fde68a]',
    imgAccent: '#C16200',
  },
  {
    id: 3,
    title: 'SC / ST / OBC Scheme',
    description: 'Targeted capital assistance and subsidy frameworks dedicated to empowering enterprises from reserved demographics.',
    tag: 'RESERVED CATEGORY',
    imgBg: 'from-[#dcfce7] to-[#bbf7d0]',
    imgAccent: '#16a34a',
  },
  {
    id: 4,
    title: 'Startup SEED Fund',
    description: 'Provision of early-stage capital allocations to support the development and commercialization of innovative ventures.',
    tag: 'EARLY STAGE',
    imgBg: 'from-[#ede9fe] to-[#ddd6fe]',
    imgAccent: '#7c3aed',
  },
  {
    id: 5,
    title: 'Tax Exemption Benefits',
    description: 'Strategic structuring of income tax, GST, and related statutory compliance exemptions for qualifying commercial entities.',
    tag: 'TAX & GST',
    imgBg: 'from-[#fee2e2] to-[#fecaca]',
    imgAccent: '#dc2626',
  },
  {
    id: 6,
    title: 'Venture Capital Support',
    description: 'Facilitation of equity financing and venture capital acquisition for enterprises demonstrating exceptional growth trajectories.',
    tag: 'EQUITY & VC',
    imgBg: 'from-[#cffafe] to-[#a5f3fc]',
    imgAccent: '#0891b2',
  },
];

function DummyImage({ bg, accent, id }: { bg: string; accent: string; id: number }) {
  return (
    <div className={`w-full h-40 rounded-sm bg-gradient-to-br ${bg} relative overflow-hidden flex items-center justify-center mb-5 shrink-0`}>
      <svg width="100%" height="100%" viewBox="0 0 320 160" xmlns="http://www.w3.org/2000/svg" className="absolute inset-0">
        <circle cx="260" cy="20" r="60" fill={accent} fillOpacity="0.12" />
        <circle cx="300" cy="130" r="40" fill={accent} fillOpacity="0.10" />
        <circle cx="30" cy="140" r="50" fill={accent} fillOpacity="0.08" />
        <rect x="20" y="20" width="90" height="6" rx="3" fill={accent} fillOpacity="0.20" />
        <rect x="20" y="34" width="60" height="6" rx="3" fill={accent} fillOpacity="0.15" />
        <rect x="20" y="48" width="75" height="6" rx="3" fill={accent} fillOpacity="0.10" />
        <rect x="110" y="50" width="100" height="75" rx="8" fill={accent} fillOpacity="0.13" />
        <rect x="122" y="63" width="76" height="8" rx="4" fill={accent} fillOpacity="0.30" />
        <rect x="122" y="78" width="55" height="8" rx="4" fill={accent} fillOpacity="0.20" />
        <rect x="122" y="93" width="65" height="8" rx="4" fill={accent} fillOpacity="0.15" />
        <circle cx="100" cy="52" r="22" fill={accent} fillOpacity="0.18" />
        <circle cx="100" cy="52" r="13" fill={accent} fillOpacity="0.22" />
      </svg>
      <span
        className="absolute bottom-3 right-3 text-[10px] font-black tracking-widest px-2.5 py-1 rounded-sm"
        style={{ background: accent, color: '#fff', opacity: 0.85 }}
      >
        SCHEME {String(id).padStart(2, '0')}
      </span>
    </div>
  );
}

export function GovernmentScheme() {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <section className="relative w-full py-12 md:py-16 overflow-hidden bg-gradient-to-br from-[#f0f7ff] via-white to-[#fff8f0]">

      {/* Background blobs */}
      <div className="pointer-events-none absolute top-0 right-0 w-96 h-96 rounded-full bg-[radial-gradient(circle,rgba(0,96,169,0.07)_0%,transparent_70%)]" />
      <div className="pointer-events-none absolute bottom-0 left-0 w-80 h-80 rounded-full bg-[radial-gradient(circle,rgba(193,98,0,0.06)_0%,transparent_70%)]" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-10 md:mb-12">
          <span className="inline-block px-5 py-2 rounded-sm text-[10px] font-black tracking-[0.18em] text-white uppercase mb-4 bg-gradient-to-r from-[#C16200] to-[#e8881a] shadow-md">
            Government Support Frameworks
          </span>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0060A9] leading-tight mb-4 tracking-tight">
            Strategic Government{' '}
            <span className="relative inline-block">
              Initiatives
              <span className="absolute -bottom-1 left-0 w-full h-1 rounded-full bg-gradient-to-r from-[#C16200] to-[#e8881a] opacity-70" />
            </span>
          </h2>

          <p className="max-w-2xl mx-auto text-sm sm:text-base text-slate-500 leading-relaxed">
            Gain structured access to over{' '}
            <span className="font-bold text-[#0060A9]">50+ verified</span> government funding,
            subsidy, and tax benefit programs — facilitated by our specialized consultancy framework.
          </p>
        </div>

       

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {schemeCards.map((scheme) => (
            <div
              key={scheme.id}
              onMouseEnter={() => setHovered(scheme.id)}
              onMouseLeave={() => setHovered(null)}
              className={`group relative rounded-sm bg-white border flex flex-col p-5 transition-all duration-300 cursor-pointer ${
                hovered === scheme.id
                  ? 'border-[#0060A9]/30 shadow-[0_12px_36px_rgba(0,96,169,0.13)] -translate-y-1'
                  : 'border-slate-200/70 shadow-sm'
              }`}
            >
              {/* Top accent bar on hover */}
              <div
                className="absolute top-0 left-0 right-0 h-0.5 rounded-t-sm transition-all duration-300"
                style={{
                  background:
                    hovered === scheme.id
                      ? `linear-gradient(90deg, ${scheme.imgAccent}, transparent)`
                      : 'transparent',
                }}
              />

              {/* Tag */}
              <span
                className="self-start text-[9px] font-black tracking-[0.15em] px-2.5 py-1 rounded-sm mb-3"
                style={{ background: scheme.imgAccent + '18', color: scheme.imgAccent }}
              >
                {scheme.tag}
              </span>

              {/* Dummy image */}
              <DummyImage bg={scheme.imgBg} accent={scheme.imgAccent} id={scheme.id} />

              {/* Title */}
              <h3 className="text-base sm:text-lg font-bold text-[#0060A9] leading-snug mb-2">
                {scheme.title}
              </h3>

              {/* Description */}
              <p className="text-xs sm:text-[13px] text-slate-500 leading-relaxed flex-1 mb-5">
                {scheme.description}
              </p>

              {/* CTA row */}
              <div className="mt-auto flex items-center justify-between">
                <a
                  href={`/schemes/${scheme.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-bold transition-all duration-300"
                  style={{ color: scheme.imgAccent }}
                >
                  Review Criteria
                  <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
                </a>
                <span className="text-[10px] font-black text-slate-300 tracking-widest">
                  {String(scheme.id).padStart(2, '0')} / 06
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="flex justify-center mt-10 md:mt-12">
          <a
            href="/services/GovernmentSchemes"
            className="group inline-flex items-center gap-3 px-8 sm:px-10 py-3.5 sm:py-4 rounded-sm text-sm sm:text-base font-bold text-white shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-r from-[#0060A9] to-[#0092D1] hover:-translate-y-0.5"
          >
            Explore All 50+ Schemes
            <span className="inline-block transition-transform duration-300 group-hover:translate-x-2">→</span>
          </a>
        </div>

      </div>
    </section>
  );
}

export default GovernmentScheme;